import type {
  ActivityResponse,
  MetricsResponse,
  SessionInfo,
} from "@shared/types";

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

async function request<T>(
  input: RequestInfo,
  init?: RequestInit
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(input, {
      credentials: "include",
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "network error";
    throw new ApiError(0, "network_error", msg);
  }

  if (!response.ok) {
    let code = "http_error";
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as ErrorBody;
      if (body.error?.code) code = body.error.code;
      if (body.error?.message) message = body.error.message;
    } catch {
      // ignore parse error
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function fetchMetrics(): Promise<MetricsResponse> {
  return request<MetricsResponse>("/api/metrics");
}

export async function fetchActivity(
  cursor?: string,
  limit = 20
): Promise<ActivityResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  return request<ActivityResponse>(`/api/activity?${params.toString()}`);
}

export async function getSession(): Promise<SessionInfo | null> {
  try {
    return await request<SessionInfo>("/auth/session");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

export async function requestMagicLink(
  email: string,
  turnstileToken: string
): Promise<void> {
  await request<{ ok: true }>("/auth/request-link", {
    method: "POST",
    body: JSON.stringify({ email, turnstileToken }),
  });
}

export async function logout(): Promise<void> {
  await request<void>("/auth/logout", { method: "POST" });
}
